import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth';
import {
  type RoutePolicy,
  type RoutePolicyRequirement,
  isAuthorizedForPolicy,
} from '../../security/routePolicyManifest';
import { type UserRole } from '../../types/roles';
import LoadingSpinner from '../ui/LoadingSpinner';

interface RoutePolicyGuardProps {
  policy?: RoutePolicyRequirement;
  anyOfPolicies?: RoutePolicyRequirement[];
  allowRoles?: UserRole[];
  children: React.ReactNode;
}

export const RoutePolicyGuard: React.FC<RoutePolicyGuardProps> = ({
  policy = 'authenticated',
  anyOfPolicies,
  allowRoles,
  children,
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#e4ebe6]"
        role="status"
        aria-live="polite"
        aria-label="Checking route access policy"
      >
        <LoadingSpinner />
      </div>
    );
  }

  const policiesToCheck = anyOfPolicies && anyOfPolicies.length > 0 ? anyOfPolicies : [policy];
  const allowsPublic = policiesToCheck.some((item) =>
    Array.isArray(item) ? item.includes('public') : item === 'public',
  );
  if (allowsPublic) {
    return <>{children}</>;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowRoles && allowRoles.includes(user.role)) {
    return <>{children}</>;
  }

  const isAuthorized = policiesToCheck.some((entry) => isAuthorizedForPolicy(user, entry));
  if (!isAuthorized) {
    return <Navigate to="/forbidden" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};
